/** Decimal amount text field helpers (SPR-1240 — safe parse, no silent magnitude changes). */

export const SUPPORT_AMOUNT_MAX = 999_999_999;
export const INVALID_AMOUNT_MESSAGE = 'סכום לא תקין. אפשר עד 2 ספרות אחרי הנקודה, עד 999,999,999';

const CURRENCY_PREFIX = /^\s*₪\s*/;
const CURRENCY_SUFFIX = /\s*ש["״]ח\s*$/;

const THOUSANDS_COMMA_PATTERN = /^\d{1,3}(,\d{3})+(\.\d{1,2})?$/;
const THOUSANDS_COMMA_INTEGER_PATTERN = /^\d{1,3}(,\d{3})+$/;

export type AmountInputEvaluation = {
  displayText: string;
  amount: number;
  error: string | null;
};

function stripCurrencyWrapper(raw: string): string {
  return raw.replace(CURRENCY_PREFIX, '').replace(CURRENCY_SUFFIX, '').trim();
}

function hasDisallowedCharacters(core: string): boolean {
  return /[^\d.,]/.test(core);
}

function isPartialAmountPattern(core: string): boolean {
  return core === '' || /^\d+$/.test(core) || /^\d+\.$/.test(core) || /^\.\d{0,2}$/.test(core) || /^\d+\.\d{0,2}$/.test(core);
}

function isValidThousandsCommaForm(core: string): boolean {
  return THOUSANDS_COMMA_PATTERN.test(core) || THOUSANDS_COMMA_INTEGER_PATTERN.test(core);
}

function parseThousandsComma(core: string): string | null {
  if (!isValidThousandsCommaForm(core)) return null;
  return core.replace(/,/g, '');
}

function normalizeToNumericString(core: string): string | null {
  const dotCount = (core.match(/\./g) ?? []).length;
  const commaCount = (core.match(/,/g) ?? []).length;

  if (dotCount > 1) return null;
  if (hasDisallowedCharacters(core)) return null;

  if (commaCount > 0) {
    return parseThousandsComma(core);
  }

  if (dotCount === 1) {
    if (!/^\d*\.?\d{0,2}$/.test(core)) return null;
    if (core === '.') return null;
    if (/^\d+\.$/.test(core)) return core.slice(0, -1);
    if (/^\.\d{1,2}$/.test(core)) return `0${core}`;
    return core;
  }

  if (!/^\d+$/.test(core)) return null;
  return core;
}

function amountFromNormalized(normalized: string): number | null {
  const n = Number(normalized);
  if (!Number.isFinite(n) || n < 0) return null;
  const decimalPart = normalized.includes('.') ? normalized.split('.')[1] : '';
  if (decimalPart.length > 2) return null;
  if (n > SUPPORT_AMOUNT_MAX) return null;
  return n;
}

export function evaluateAmountInput(raw: string): AmountInputEvaluation {
  if (raw === '') {
    return { displayText: '', amount: 0, error: null };
  }

  const core = stripCurrencyWrapper(raw);

  if (/[eExX+-]/.test(core) || hasDisallowedCharacters(core)) {
    return { displayText: raw, amount: 0, error: INVALID_AMOUNT_MESSAGE };
  }

  if (core.includes(',') && !isValidThousandsCommaForm(core)) {
    return { displayText: raw, amount: 0, error: INVALID_AMOUNT_MESSAGE };
  }

  const normalized = normalizeToNumericString(core);
  if (normalized === null) {
    if (isPartialAmountPattern(core)) {
      const partialAmount = core.endsWith('.') ? Number(core.slice(0, -1)) : core.startsWith('.') ? Number(`0${core}`) : Number(core);
      if (Number.isFinite(partialAmount) && partialAmount >= 0 && partialAmount <= SUPPORT_AMOUNT_MAX) {
        return { displayText: raw, amount: partialAmount, error: null };
      }
    }
    return { displayText: raw, amount: 0, error: INVALID_AMOUNT_MESSAGE };
  }

  const amount = amountFromNormalized(normalized);
  if (amount === null) {
    return { displayText: raw, amount: 0, error: INVALID_AMOUNT_MESSAGE };
  }

  return { displayText: raw, amount, error: null };
}

/** @deprecated use evaluateAmountInput */
export function sanitizeDecimalAmountInput(raw: string): string {
  const result = evaluateAmountInput(raw);
  return result.error ? raw : result.displayText;
}

/** @deprecated use evaluateAmountInput */
export function parseDecimalAmountInput(rawOrSanitized: string): number {
  return evaluateAmountInput(rawOrSanitized).amount;
}

export function amountToInputText(amount: number, fieldText: string | null): string {
  if (fieldText !== null) return fieldText;
  return formatAmountDisplay(amount);
}

export function formatAmountDisplay(amount: number): string {
  if (amount === 0) return '';
  if (!Number.isFinite(amount) || amount > SUPPORT_AMOUNT_MAX) return '';
  const rounded = Math.round(amount * 100) / 100;
  const fixed = rounded.toFixed(2);
  return fixed.replace(/\.?0+$/, '');
}

export function isValidSubmittedAmount(amount: number): boolean {
  if (!Number.isFinite(amount) || amount <= 0 || amount > SUPPORT_AMOUNT_MAX) return false;
  const cents = Math.round(amount * 100);
  return Math.abs(amount * 100 - cents) < 1e-4;
}
